import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { change, FieldArray } from 'redux-form';
import cn from 'classnames';
import { CheckboxItem } from 'chameleon/ui-stack/ui/forms/check-box-list';
import { useTreeListBox } from 'chameleon/ui-stack/utilities/a11y';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';
import StudentsDropdown from '../StudentsDropdown';
import { FORM_FIELDS_MAP } from '../../utils/constants';
import {
  calculateClassesStats,
  getFormIdField,
  getSectionStudents,
} from '../../utils';
import getSectionName from '../../utils/getSectionName';
import Context from '../../Context';

const getAssigneesFieldData = ({
  section,
  updateSections,
  updateAssigneesName,
  assigneesName,
  updateAssignees,
  assignees,
}) => {
  const isUpdateSection = updateSections.indexOf(section) >= 0;
  const actualAssigneesFieldName = isUpdateSection
    ? updateAssigneesName
    : assigneesName;
  const actualAssigneesList = [
    ...(isUpdateSection ? updateAssignees : assignees),
  ];
  const actualAssigneeIds = actualAssigneesList.map((assignee) => assignee.id);
  const indexOfActualAssignee = actualAssigneeIds.indexOf(section.id);

  return {
    actualAssigneesFieldName,
    actualAssigneesList,
    indexOfActualAssignee,
  };
};

/**
 * Sections list with checkboxes.
 * */
const SectionsFields = (props) => {
  const [expandedSectionIds, setExpandedSectionIds] = useState([]);
  const {
    allSections,
    updateSections,
    assignees,
    assigneesName,
    updateAssignees,
    updateAssigneesName,
    selection,
    setSelection,
    isFinalExam,
    isMultipleSelection = false,
    meta: { form },
    fields,
    dispatch,
    subjectClassList, // students list in each classes
    studentsFormData = [], // students list in redux form
    individualStudentAssignmentFlag,
    setSelectedAssignment,
    fetchSingleAssignmentData,
    openAssignments,
    selectedAssignment,
    allAssigneesName,
    isAssign,
    disabledStudents,
    selectedSectionCurrentStudentsLength,
    setIsOpened,
    optionContainerRef,
  } = props;
  const { classesStats, dispatchClassesStats } = useContext(Context);
  const allAssignees = fields.getAll() || [];
  const disabledStudentIds = disabledStudents.map((student) => student.id);

  const { treeItemProps } = useTreeListBox({
    onExpand: (event) => {
      const expandCollapseButton = event.target.querySelector(
        '.expand-collapse-button',
      );
      if (expandCollapseButton) {
        expandCollapseButton.click();
      }
    },
    onCollapse: (event) => {
      const expandCollapseButton = event.target.querySelector(
        '.expand-collapse-button',
      );
      if (expandCollapseButton) {
        expandCollapseButton.click();
      }
    },
    onClick: (event) => {
      const checkboxElem = event.target.querySelector('[role="checkbox"]');
      if (checkboxElem) {
        checkboxElem.click();
      }
    },
  });

  /**
   * Toggles a section selection.
   * @param {object} event - The HTML event.
   * @param {array} formFields - The list of form fields.
   * @param {number} formFieldIndex - The index of the field to toggle.
   * @param {bool} isChecked - Current state of the selection.
   * @param {object} section - The section to toggle.
   * */
  const onToggle = (event, formFields, formFieldIndex, isChecked, section) => {
    event.preventDefault();

    const {
      actualAssigneesFieldName,
      actualAssigneesList,
      indexOfActualAssignee,
    } = getAssigneesFieldData({
      section,
      updateSections,
      updateAssigneesName,
      assigneesName,
      updateAssignees,
      assignees,
    });
    const sectionStudents = getSectionStudents(
      subjectClassList,
      section.sectionId,
    );
    const { assignmentId: sectionAssignmentId } = section;
    const availableSectionStudents = isAssign
      ? sectionStudents.filter((sectionStudent) =>
          sectionAssignmentId
            ? true
            : !disabledStudentIds.includes(
                getFormIdField(sectionStudent.id, section.sectionId),
              ),
        )
      : sectionStudents;
    let newStudentsFormData;

    /* If `isChecked` is true we're actually removing the class from the redux-form. */
    if (isChecked) {
      actualAssigneesList.splice(indexOfActualAssignee, 1);
      formFields.remove(formFieldIndex);

      /*
       * When removing a class from the redux-form we shall also remove the students within that class.
       * The selected students within the form are found in `studentsFormData`.
       * Therefore we need to filter them and keep ONLY the ones which meet the following criteria:
       *   - are not part of the section/assignment we're removing
       *   - have `status` property equal to true because those are disabled (e.g. they already started the assignment.)
       * */
      const sectionStudentIds = availableSectionStudents.map(
        (student) => student.id,
      );
      const assignmentId = selectedAssignment.id || section.assignmentId;
      newStudentsFormData = studentsFormData.filter((s) => {
        if (s.status === true) {
          return true;
        }

        if (sectionStudentIds.includes(s.studentId)) {
          // Assignment ID may not be present in case of assignation.
          // Don't include assignment ID in exclusion if not available.
          if (isAssign) {
            return !!s.assignmentId
              ? sectionAssignmentId
                ? s.sectionId !== section.sectionId &&
                  s.assignmentId !== sectionAssignmentId
                : true
              : sectionAssignmentId
                ? true
                : s.sectionId !== section.sectionId;
          }

          return (
            s.sectionId !== section.sectionId && s.assignmentId !== assignmentId
          );
        }

        return true;
      });
    } else {
      actualAssigneesList.push(section);
      formFields.push(section);

      /*
       * When we add a new class/assignment to the form we need to also add all the students within it to the redux-form.
       * We need to add only the students that are enabled (they have `status` property equal to false).
       * The student field data within the form shall have the following structure (beside it's own fields):
       * {
       *   id: STUDENT_ID__SECTION_ID__ASSIGNMENT_ID     - this is used for unique identify students within the form
       *   studentId: STUDENT_ID                         - this is the actual ID of the student
       *   sectionId: SECTION_ID                         - this is the section ID that the student belongs to
       *   assignmentId: ASSIGNMENT_ID                   - this is the assignment ID that the student belongs to
       * }
       * */
      const assignmentId = selectedAssignment.id || section.assignmentId;
      const enabledStudents = availableSectionStudents.filter(
        (student) =>
          !disabledStudentIds.includes(
            getFormIdField(student.id, student.sectionId, assignmentId),
          ),
      );
      const formStudentIds = studentsFormData.map((student) => student.id);
      newStudentsFormData = studentsFormData.concat(
        enabledStudents
          .filter(
            (student) =>
              !formStudentIds.includes(
                getFormIdField(student.id, section.sectionId, assignmentId),
              ),
          )
          .map((student) => ({
            ...student,
            id: getFormIdField(student.id, section.sectionId, assignmentId),
            studentId: student.id,
            sectionId: section.sectionId,
            assignmentId,
          })),
      );
    }

    dispatch(change(form, actualAssigneesFieldName, actualAssigneesList));
    dispatch(change(form, FORM_FIELDS_MAP.students, newStudentsFormData));

    const previousCheckStatus = !isChecked;
    calculateClassesStats({
      dispatch: dispatchClassesStats,
      step: previousCheckStatus ? 'add' : 'remove',
      stats: classesStats,
      students: availableSectionStudents,
    });
  };

  const onOptionsContainerBlur = (e) => {
    const { relatedTarget } = e;

    const isContained =
      relatedTarget &&
      optionContainerRef.current.compareDocumentPosition(relatedTarget) &
        Node.DOCUMENT_POSITION_CONTAINED_BY;
    if (!isContained) {
      setIsOpened(false);
    }
  };

  const onMouseDown = (event) => {
    event.preventDefault();
  };

  return (
    <ul
      className="list-none p-0 m-0 -mx-4.2"
      tabIndex={0}
      role="tree"
      aria-activedescendant=""
      aria-multiselectable="true"
      onMouseDown={onMouseDown}
      onBlur={
        optionContainerRef && optionContainerRef.current
          ? onOptionsContainerBlur
          : null
      }
      {...treeItemProps}
    >
      {allSections.map((section) => {
        const { id: formId, sectionId } = section;
        const fieldIndex = allAssignees.findIndex(
          (field) => field.id === formId,
        );
        const isChecked = fieldIndex >= 0;

        const handleSelection = (
          selectedFormId,
          selectedSectionId = null,
          selectedAssignmentId = null,
        ) => {
          if (isFinalExam) {
            setSelection(
              !(selection && selection === selectedSectionId)
                ? selectedSectionId
                : null,
            );
          } else if (!!openAssignments && individualStudentAssignmentFlag) {
            const assignment = openAssignments.find(
              (openAssignment) => openAssignment.id === selectedAssignmentId,
            );
            if (
              Number.parseInt(selectedAssignment.id, 10) !==
                selectedAssignmentId &&
              individualStudentAssignmentFlag
            ) {
              setSelectedAssignment && setSelectedAssignment(assignment || {});
              fetchSingleAssignmentData &&
                fetchSingleAssignmentData((assignment || {}).id);
            }
          }
        };

        const subjectClass =
          (subjectClassList || []).find(
            (g) => parseInt(g.id, 10) === parseInt(sectionId, 10),
          ) || {};
        const students = (subjectClass.students || []).map((student) => ({
          ...student,
          sectionId: subjectClass.id,
        }));

        const onHandleExpandCollapseStudentView = (collapseKey, event) => {
          if (event) {
            const expanderElem = event.currentTarget.closest('[aria-expanded]');
            if (expanderElem) {
              const isAriaExpanded =
                expanderElem.getAttribute('aria-expanded') === 'true';
              expanderElem.setAttribute(
                'aria-expanded',
                isAriaExpanded ? 'false' : 'true',
              );
            }
            event.preventDefault();
            event.stopPropagation();
          }

          if (expandedSectionIds.includes(collapseKey)) {
            const collapseKeyIndex = expandedSectionIds.indexOf(collapseKey);

            setExpandedSectionIds([
              ...expandedSectionIds.slice(0, collapseKeyIndex),
              ...expandedSectionIds.slice(collapseKeyIndex + 1),
            ]);
          } else {
            setExpandedSectionIds([...expandedSectionIds, collapseKey]);
          }
        };

        let isCollapsed;
        if (expandedSectionIds.length === 0) {
          isCollapsed = true;
        } else {
          isCollapsed = !expandedSectionIds.includes(formId);
        }

        const isCollapsible =
          individualStudentAssignmentFlag &&
          students.length > 0 &&
          !isFinalExam;
        const checkboxExpandableProps = {
          isCollapsed,
          // show the collapsible checkbox if there are students in that class
          isCollapsible,
          onCollapseExpand: onHandleExpandCollapseStudentView,
          collapseKey: formId,
          individualStudentAssignmentFlag,
        };

        const sectionName = getSectionName({
          isFinalExam,
          individualStudentAssignmentFlag,
          section,
          isAssign,
          studentsFormData,
          subjectClassList,
          disabledStudentIds,
        });
        const selectedAssignmentSectionId =
          selectedAssignment.section_id ||
          (selectedAssignment.group_ids || [])[0];
        const selectedAssignmentFormId = selectedAssignmentSectionId
          ? getFormIdField(selectedAssignmentSectionId, selectedAssignment.id)
          : null;

        const isCurrentItem = selectedAssignmentFormId === formId;
        const isManageFormAndCurrentItem = !isAssign && isCurrentItem;
        const withNameClickEvent =
          individualStudentAssignmentFlag &&
          (isAssign || isManageFormAndCurrentItem);

        const isActiveItem = individualStudentAssignmentFlag && isCurrentItem;
        const checkboxClassName = cn(
          'hover:bg-gray-200 pl-4.2 py-4 pr-0 cursor-pointer',
          {
            'border-b border-gray-300': isCollapsed,
          },
        );
        const checkboxProps = {
          id: formId,
          name: sectionName,
          fields,
          section,
          onToggle,
          isChecked,
          selection,
          fieldIndex,
          isFinalExam,
          handleSelection,
          isMultipleSelection,
          isActiveItem,
          toggleOnlyOnActive: !isAssign,
          onNameClick: withNameClickEvent
            ? onHandleExpandCollapseStudentView
            : null,
          isClickDisabled:
            !isAssign &&
            individualStudentAssignmentFlag &&
            !isFinalExam &&
            isCurrentItem,
          ...checkboxExpandableProps,
          isHiddenCheckbox: !isAssign,
          isDropdownIconInvisible: !isAssign && !isActiveItem,
          tabIndex: -1,
          className: checkboxClassName,
          color: 'blue',
        };

        const checkboxClassWithStudents = cn('ft-cd-option', {
          'with-students-dropdown': isCollapsible && !isCollapsed,
        });

        const showStudentsDropdown =
          !isCollapsed && individualStudentAssignmentFlag && !isFinalExam;
        const {
          actualAssigneesFieldName,
          actualAssigneesList,
          indexOfActualAssignee,
        } = getAssigneesFieldData({
          section,
          updateSections,
          updateAssigneesName,
          assigneesName,
          updateAssignees,
          assignees,
        });

        return (
          <li
            id={formId} //used to notify parent with role="tree" aria-activedescendant
            role="treeitem"
            tabIndex={-1}
            aria-selected={false}
            aria-expanded={false}
            data-test-id={DATA_TEST_IDS.ASSIGN_TO_SECTION}
            key={formId}
            className={checkboxClassWithStudents}
          >
            <CheckboxItem {...checkboxProps} />
            {showStudentsDropdown && (
              <FieldArray
                component={StudentsDropdown}
                name={FORM_FIELDS_MAP.students}
                props={{
                  students,
                  section,
                  actualAssigneesFieldName,
                  actualAssigneesList,
                  indexOfActualAssignee,
                  allAssigneesName,
                  allAssignees,
                  dispatch,
                  studentsFormData,
                  individualStudentAssignmentFlag,
                  selectedAssignment,
                  disabledStudents,
                  sectionFieldIndex: fieldIndex,
                  isSectionChecked: isChecked,
                  onToggleSection: onToggle,
                  sectionFields: fields,
                  selectedSectionCurrentStudentsLength,
                  isAssign,
                  subjectClassList,
                }}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};

SectionsFields.propTypes = {
  setSelection: PropTypes.func,
  selection: PropTypes.any,
  selectedAssignment: PropTypes.object,
  selectedSectionCurrentStudentsLength: PropTypes.number,
  setIsOpened: PropTypes.func,
};

SectionsFields.defaultProps = {
  setSelection: () => {},
  selection: 0,
  selectedAssignment: {},
  selectedSectionCurrentStudentsLength: 0,
  setIsOpened: () => {},
};

export default SectionsFields;
